import { PRICING_METHODS } from "./AppliedExcessRecorder";
import CentredLoader from "./CentredLoader";
import {
  deletionMappingsReducer,
  getConversionTypeForColumn,
} from "./ClaimsTransformerSupport";
import DataValidation from "./DataValidation";
import { Box, Button, Stack, Typography } from "@mui/material";
import * as config from "config";
import { CLAIMS_MAPPING_IGNORE_VALUE, EXCLUDE_KEY } from "config";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import {
  useDoConversionQuery,
  useGetClaimsMappingQuery,
  useGetConversionErrorsQuery,
  useGetHeaderRowQuery,
} from "services/tablesService";
import { setV2ClaimsKey } from "store/actions/input/claims/claimsActions";

const useProgress = ({
  isMultiSheet,
  totalSheets,
  currentSheetIndex,
  tableName,
  completedSheets,
}) => {
  const [phase, setPhase] = useState("initializing");
  const [details, setDetails] = useState("");

  const [message, setMessage] = useState("");

  const setProgress = useCallback(
    (phase, details = "") => {
      setPhase(phase);
      setDetails(details);
    },
    [setPhase, setDetails]
  );

  // Update progress message
  useEffect(() => {
    const sheetInfo =
      isMultiSheet && totalSheets > 1
        ? `[Sheet ${currentSheetIndex + 1}/${totalSheets}: ${tableName}] `
        : tableName
        ? `[${tableName}] `
        : "";

    const completedInfo =
      completedSheets.length > 0
        ? `(${completedSheets.length} sheets completed) `
        : "";

    const newMessage = `${sheetInfo}${completedInfo}${phase}${
      details ? ` - ${details}` : ""
    }`;
    setMessage(newMessage);
  }, [
    isMultiSheet,
    totalSheets,
    currentSheetIndex,
    tableName,
    completedSheets,
    phase,
    details,
  ]);

  return {
    phase,
    message,
    setProgress,
  };
};

export const TIMEOUT_MS = 120000; // 2 minutes timeout

export const useTimeout = ({ progressPhase, setProgress }) => {
  const [isTimeout, setIsTimeout] = useState(false);
  const timeoutHandleRef = useRef(null);
  const [timeoutPhase, setTimeoutPhase] = useState(null);

  // (Re)arm a fresh timeout, clearing any existing one. Using a ref for the
  // handle means rapid re-arms (e.g. from progress notifications) always clear
  // the latest timer rather than a stale one.
  const armTimeout = useCallback(() => {
    if (timeoutHandleRef.current) {
      clearTimeout(timeoutHandleRef.current);
    }
    timeoutHandleRef.current = setTimeout(() => {
      setProgress(
        "processing timeout",
        "operation taking longer than expected"
      );
      setIsTimeout(true);
    }, TIMEOUT_MS);
  }, [setProgress]);

  // Set up timeout for stuck processing detection
  useEffect(() => {
    // Set a timeout for phases that might get stuck
    const stuckPhases = [
      "validating data",
      "processing data",
      "generating mapping table",
    ];
    if (stuckPhases.includes(progressPhase) && progressPhase !== timeoutPhase) {
      armTimeout();
      setTimeoutPhase(progressPhase);
    }
  }, [progressPhase, timeoutPhase, armTimeout]);

  const resetTimeout = useCallback(() => {
    if (timeoutHandleRef.current) {
      clearTimeout(timeoutHandleRef.current);
      timeoutHandleRef.current = null;
    }
    setIsTimeout(false);
    setTimeoutPhase(null);
  }, []);

  // Restart the timeout window without otherwise disturbing state. Called while
  // a legitimately slow task is still making progress, so the 2 minute timer
  // counts down afresh from the last sign of activity.
  const restartTimeout = useCallback(() => {
    armTimeout();
  }, [armTimeout]);

  return { isTimeout, resetTimeout, restartTimeout };
};

export const ClaimsTransformer = ({
  fileId,
  origin,
  tableName,
  headerRows,
  mapping,
  appliedExcess,
  substitutions = [],
  rowDeletions = [],
  filename,
  originalFileId = null,
  originalFilename = null,
  extractionDetails = null,
  tablesList,
  onConversionComplete,
  setDateConversionType,
  dateConversionTypeList = {},
  onSubstitutionsChanged,
  onRowDeletionsChanged,
  // New props for better progress tracking
  isMultiSheet = false,
  currentSheetIndex = 0,
  totalSheets = 1,
  completedSheets = [],
  selectedSheets = [],
  setCurrentSheetIndex = () => {},
  sheetConfigs = {},
}) => {
  const [columnMappings, setColumnMappings] = useState({});
  const [deletionMappings, setDeletionMappings] = useState({});
  const [openClosedMapping, setOpenClosedMapping] = useState(null);
  const [coverageMapping, setCoverageMapping] = useState(null);
  const [coverageDefault, setCoverageDefault] = useState(null);
  const [requestDateConversionTypes, setRequestDateConversionTypes] = useState(
    []
  );
  const [requestSubstitutions, setRequestSubstitutions] = useState([]);
  const [requestDeletions, setRequestDeletions] = useState([]);

  const {
    phase: progressPhase,
    message: progressMessage,
    setProgress,
  } = useProgress({
    isMultiSheet,
    totalSheets,
    currentSheetIndex,
    tableName,
    completedSheets,
  });

  const { isTimeout, resetTimeout, restartTimeout } = useTimeout({
    progressPhase,
    setProgress,
  });

  // Tracks the highest rowsProcessed seen so far for the current conversion so
  // we can detect when a legitimately slow task is still making progress.
  const lastRowsProcessedRef = useRef(0);

  useEffect(() => {
    setProgress("preparing column mappings");
    const colMap = {};
    if (mapping) {
      // Processing sheet with column mappings
      for (const index in mapping) {
        const entry = mapping[index];
        colMap[entry.outputName] = entry.inputColumn;
      }
      setColumnMappings(colMap);
    } else {
      // No mapping provided for sheet
    }
  }, [mapping, setColumnMappings, tableName, setProgress]);

  const { data: headings } = useGetHeaderRowQuery(
    {
      fileId,
      tableName,
      skipRows: origin?.row ?? 0,
      skipColumns: origin?.column ?? 0,
      headerRows,
    },
    { skip: !fileId || !tableName }
  );

  const addDeletionMapping = useCallback(
    (key, deletions) => {
      const excludeColumn = columnMappings[key];
      if (!excludeColumn && excludeColumn !== 0) return;
      setDeletionMappings(deletionMappingsReducer(excludeColumn, deletions));
    },
    [columnMappings]
  );

  useEffect(() => {
    if (Object.keys(columnMappings ?? {}).length > 0) {
      setProgress("configuring row deletions");
      if (EXCLUDE_KEY in columnMappings) {
        const excMapping = getConversionTypeForColumn(
          mapping,
          EXCLUDE_KEY,
          "IN_DELETE_ROW"
        );
        if (!excMapping) return;
        const excVals = Object.entries(excMapping?.conversions)
          .filter(([_, func]) => func === CLAIMS_MAPPING_IGNORE_VALUE)
          .map(([val, _]) => val);
        if (excVals.length > 0) {
          addDeletionMapping(EXCLUDE_KEY, excVals);
        }
      }
    }
  }, [
    columnMappings,
    setDeletionMappings,
    deletionMappings,
    mapping,
    addDeletionMapping,
    setProgress,
  ]);

  useEffect(() => {
    if (Object.keys(columnMappings ?? {}).length > 0) {
      setProgress("configuring value mappings");
      const generateMappingDataForEnumCol = (
        colName,
        mappingSetter,
        existing
      ) => {
        const enMapping = getConversionTypeForColumn(
          mapping,
          colName,
          "CONVERT"
        )?.conversions;
        if (!enMapping) return;
        const disFilter = Object.entries(enMapping)
          .filter(
            ([_, mapped]) => mapped === config.CLAIMS_MAPPING_IGNORE_VALUE
          )
          .map(([val, _]) => val);
        addDeletionMapping(colName, disFilter);
        const enFilter = Object.fromEntries(
          Object.entries(enMapping).filter(
            ([_, mapped]) => mapped !== config.CLAIMS_MAPPING_IGNORE_VALUE
          )
        );
        if (JSON.stringify(enFilter) !== JSON.stringify(existing ?? {})) {
          mappingSetter(enFilter);
        }
      };

      generateMappingDataForEnumCol(
        "_OPEN_CLOSED",
        setOpenClosedMapping,
        openClosedMapping
      );

      const coverageConversion = getConversionTypeForColumn(
        mapping,
        "_COVERAGE",
        "CONSTANT"
      );

      if (coverageConversion) {
        setCoverageDefault(coverageConversion.value);
      } else {
        generateMappingDataForEnumCol(
          "_COVERAGE",
          setCoverageMapping,
          coverageMapping
        );
      }
    }
  }, [
    columnMappings,
    mapping,
    setOpenClosedMapping,
    setCoverageMapping,
    coverageMapping,
    openClosedMapping,
    addDeletionMapping,
    setProgress,
  ]);

  const hasCoverage =
    Object.keys(coverageMapping ?? {}).length > 0 ||
    coverageDefault != null ||
    (mapping && mapping.length > 0);

  const getExcess = () => {
    switch (appliedExcess?.type) {
      case PRICING_METHODS.FGU:
        return 0;
      case PRICING_METHODS.EXCESS:
        return appliedExcess?.excessRemoved ? appliedExcess?.excess : 0;
      default:
        return null;
    }
  };

  const getThreshold = () => {
    switch (appliedExcess?.type) {
      case PRICING_METHODS.FGU:
        return 0;
      case PRICING_METHODS.EXCESS:
        return appliedExcess?.excess;
      default:
        return null;
    }
  };

  const {
    data: mappingTable,
    status: mappingTableStatus,
  } = useGetClaimsMappingQuery(
    {
      fileId,
      columnMappings,
      substitutions: requestSubstitutions,
      rowDeletions: requestDeletions,
      deletionMappings: deletionMappings ? Object.values(deletionMappings) : [],
      openClosedMapping,
      openClosedDeleteUnmappedRows: false,
      coverageMapping: coverageMapping ?? [],
      coverageDeleteUnmappedRows: false,
      coverageDefault: coverageDefault ?? "GL",
      excess: getExcess(),
      threshold: getThreshold(),
      defaultDate: "1970-01-01",
      dateConversionTypes: requestDateConversionTypes,
    },
    {
      skip: !hasCoverage || !fileId,
    }
  );

  // Update progress when mapping table status changes
  useEffect(() => {
    if (mappingTableStatus === "pending") {
      setProgress("generating mapping table");
    } else if (mappingTableStatus === "fulfilled") {
      setProgress("mapping table ready");
    }
  }, [mappingTableStatus, setProgress]);

  const {
    data: conversionErrors,
    status: conversionErrorsStatus,
  } = useGetConversionErrorsQuery(
    {
      fileId,
      tableName,
      skipColumns: origin?.column ?? 0,
      skipRows: origin?.row ?? 0,
      headerRows,
      skipBlankRows: true,
      mapping: mappingTable,
    },
    { skip: mappingTableStatus !== "fulfilled" || !fileId || !tableName }
  );

  // Update progress when conversion errors status changes
  useEffect(() => {
    if (conversionErrorsStatus === "pending") {
      setProgress("validating data");
    } else if (conversionErrorsStatus === "fulfilled") {
      // Validation has settled; stop the timer so it can't fire while the user
      // is fixing errors (validation errors require user input) or after a
      // clean validation completes.
      resetTimeout();
      const hasErrors = conversionErrors?.errors?.length > 0;
      if (hasErrors) {
        setProgress("validation errors found");
      } else {
        setProgress("validation complete");
      }
    } else if (conversionErrorsStatus === "rejected") {
      resetTimeout();
    }
  }, [conversionErrorsStatus, conversionErrors, setProgress, resetTimeout]);

  const hasConversionErrors =
    conversionErrorsStatus === "fulfilled" && conversionErrors?.errors?.length;

  const {
    data: processingResponse,
    status: processingResponseStatus,
  } = useDoConversionQuery(
    {
      fileId,
      tableName,
      skipColumns: origin?.column ?? 0,
      skipRows: origin?.row ?? 0,
      headerRows,
      skipBlankRows: true,
      mapping: mappingTable,
      progressNotification: (timing) => {
        if (timing?.rowsProcessed) {
          // Still chewing through rows on a legitimately slow task: give it a
          // fresh 2 minute window from the last time progress advanced.
          if (timing.rowsProcessed > lastRowsProcessedRef.current) {
            restartTimeout();
          }
          lastRowsProcessedRef.current = timing.rowsProcessed;
          if (timing.percentageComplete === 100) {
            setProgress("saving data", "process complete");
          } else {
            setProgress(
              "processing rows",
              `${timing.rowsProcessed} rows processed, ${timing.percentageComplete}% complete, ${timing.remainingSeconds}s remaining`
            );
          }
        } else {
          setProgress("starting conversion", "initializing");
        }
      },
    },
    {
      skip:
        mappingTableStatus !== "fulfilled" ||
        hasConversionErrors ||
        !fileId ||
        !tableName,
    }
  );

  // Update progress when processing response status changes
  useEffect(() => {
    if (processingResponseStatus === "pending") {
      lastRowsProcessedRef.current = 0;
      setProgress("processing data");
    } else if (processingResponseStatus === "fulfilled") {
      // Conversion finished; stop the timer so it can't fire post-completion.
      resetTimeout();
      setProgress("processing complete");
    } else if (processingResponseStatus === "rejected") {
      resetTimeout();
      setProgress("processing failed");
    }
  }, [processingResponseStatus, setProgress, resetTimeout]);

  const dispatch = useDispatch();
  const [createdFile, setCreatedFile] = useState(null);
  useEffect(() => {
    if (
      processingResponseStatus === "fulfilled" &&
      processingResponse.createdFile &&
      createdFile !== processingResponse.createdFile
    ) {
      setCreatedFile(processingResponse.createdFile);
      setProgress("finalizing", "updating claims data");

      dispatch(
        setV2ClaimsKey(
          processingResponse.createdFile,
          fileId,
          tableName,
          filename,
          tablesList,
          originalFileId,
          originalFilename,
          extractionDetails
        )
      );
      onConversionComplete();
    }
  }, [
    createdFile,
    setCreatedFile,
    processingResponse,
    processingResponseStatus,
    dispatch,
    fileId,
    tableName,
    filename,
    tablesList,
    originalFileId,
    originalFilename,
    extractionDetails,
    onConversionComplete,
    setProgress,
  ]);

  const setRequestValues = () => {
    setProgress("preparing reprocessing");
    const activeSubstitutions = substitutions
      .filter((x) => !rowDeletions.includes(x.row))
      .reduce((acc, { column, row, value }) => {
        if (!acc[column]) {
          acc[column] = { column, replacements: [] };
        }
        acc[column].replacements.push({ row, value });
        return acc;
      }, {});

    const dateConversions = Object.entries(dateConversionTypeList).map(
      ([column, type]) => ({
        column,
        type,
      })
    );

    setRequestDateConversionTypes(dateConversions);
    setRequestSubstitutions(Object.values(activeSubstitutions));
    setRequestDeletions(rowDeletions);
  };

  if (
    processingResponseStatus === "rejected" ||
    conversionErrorsStatus === "rejected"
  ) {
    setProgress("error", "failed to process data");
    return <CentredLoader label={progressMessage} error={true} />;
  } else if (isTimeout) {
    return (
      <CentredLoader
        label={progressMessage}
        error={true}
        extraComponents={
          <Stack direction={"column"} gap={2} sx={{ marginTop: 5 }}>
            <Typography
              variant={"body1"}
              color={"text.secondary"}
              align={"center"}
            >
              {
                "The processing operation is taking longer than expected. This may be due to a large file or server load."
              }
            </Typography>
            <Box display={"flex"} justifyContent={"center"}>
              <Button
                variant={"contained"}
                color={"primary"}
                disableElevation
                onClick={() => {
                  resetTimeout();
                  setProgress("retrying", "restarting validation");
                }}
              >
                {"Retry Processing"}
              </Button>
            </Box>
          </Stack>
        }
      />
    );
  } else if (conversionErrorsStatus !== "fulfilled") {
    return <CentredLoader label={progressMessage} />;
  } else if (hasConversionErrors) {
    return (
      <DataValidation
        conversionErrors={conversionErrors}
        headings={headings}
        origin={origin}
        substitutions={substitutions}
        rowDeletions={rowDeletions}
        dateConversionTypeList={dateConversionTypeList}
        onReProcess={setRequestValues}
        onSubstitutionsChanged={(substitutions, sheetName) =>
          onSubstitutionsChanged(substitutions, sheetName)
        }
        onRowDeletionsChanged={(rowDeletions, sheetName) =>
          onRowDeletionsChanged(rowDeletions, sheetName)
        }
        setDateConversionType={(column, type, sheetName) =>
          setDateConversionType(column, type, sheetName)
        }
        isMultiSheet={isMultiSheet}
        selectedSheets={selectedSheets}
        currentSheetIndex={currentSheetIndex}
        setCurrentSheetIndex={setCurrentSheetIndex}
        sheetConfigs={sheetConfigs}
      />
    );
  } else {
    return <CentredLoader label={progressMessage} />;
  }
};

export default ClaimsTransformer;
